import { localWorkspaceService } from '@/services/LocalWorkspaceService';
import { pdfCache } from '@/services/PdfCacheService';
import { bucketCount, bucketMb, classifyError, track } from '@/analytics';
import type { Markup } from '@/types';
import { browserDefaultUnitSystem } from '@/core/units';
import { UnitSystem } from '@/types';
import { assetStore } from '@/services/assets/IdbAssetAdapter';
import { ingest } from '@/core/image/ingest';
import { setImportNotices } from './importNotices';
import { pdfjsAssetOptions } from '@/utils/pdfjsAssets';

/** Encode a local PDF file name into its `/local/:slug` viewer path. */
export function localViewerPath(fileName: string): string {
    return `/local/${encodeURIComponent(fileName)}`;
}

function trackLocalOpenSuccess(pageCount: number, sizeBytes: number, seededMarkupsCount: number): void {
    try {
        track('document_uploaded', {
            page_bucket: bucketCount(pageCount),
            size_bucket_mb: bucketMb(sizeBytes / 1000000),
            seeded_markups_bucket: bucketCount(seededMarkupsCount),
        });
    } catch {
        // Analytics must never block the local open flow.
    }
}

function trackLocalOpenFailure(err: unknown): void {
    try {
        track('operation_failed', {
            operation: 'local_document_open',
            error_kind: classifyError(err),
        });
    } catch {
        // Preserve the original open failure even if analytics fails.
    }
}

/**
 * Persist a local PDF into the browser workspace and return the viewer route.
 * The landing pages use this path.
 */
export async function openLocalPdf(file: File, unitSystem: UnitSystem = browserDefaultUnitSystem()): Promise<string> {
    try {
        await localWorkspaceService.init();
        const original = await file.arrayBuffer();
        const [{ getDocument, version: pdfjsVersion }, { configurePdfjsWorker }, { importMarkupsFromPdf, stripReportPages, recoverMediaAssets, lostPhotosNotice }] = await Promise.all([
            import('pdfjs-dist'),
            import('@bim-pdf/renderer'),
            import('@/core/pdf-import'),
        ]);
        configurePdfjsWorker(
            new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString(),
        );

        // Our own export's photo report is not part of the drawing set: strip it when its page markers and the
        // sidecar agree, else keep every page and say so (image markup spec 4.7).
        const strip = await stripReportPages(original.slice(0));
        const notices = strip.notice ? [strip.notice] : [];
        const arrayBuffer = strip.bytes === original ? original.slice(0) : strip.bytes;

        // getDocument detaches arrayBuffer into the worker; keep a copy for the
        // /Annot interop fallback (Bluebeam/Acrobat/etc.).
        const annotBytes = arrayBuffer.slice(0);
        const loadingTask = getDocument({ data: arrayBuffer, ...pdfjsAssetOptions(pdfjsVersion) });
        const pdfDoc = await loadingTask.promise;
        const pageCount = pdfDoc.numPages;
        let importedMarks = await importMarkupsFromPdf(pdfDoc, annotBytes);
        pdfDoc.destroy();

        // Photos this device does not hold come back from the PDF itself (the ORIGINAL bytes: the report pages
        // carry the photo-pin photos), as they are or re-encoded through Ingest.
        const recovered = await recoverMediaAssets(original, importedMarks, { store: assetStore, ingest });
        importedMarks = recovered.markups;
        if (recovered.lost > 0) notices.push(lostPhotosNotice(recovered.lost));
        setImportNotices(notices);

        await localWorkspaceService.clearWorkspace();
        await pdfCache.clear();

        const pdfBlobKey = `local-${Date.now()}`;
        // Use cachePdfRequired so a quota-exceeded or IDB failure surfaces here rather
        // than silently producing a workspace that points at a blob that was never stored.
        await pdfCache.cachePdfRequired(pdfBlobKey, strip.stripped > 0
            ? new File([strip.bytes], file.name, { type: 'application/pdf', lastModified: file.lastModified })
            : file);

        await localWorkspaceService.createWorkspace(
            pdfBlobKey,
            file.name,
            pageCount,
            unitSystem,
        );

        const now = new Date();
        for (const mark of importedMarks) {
            await localWorkspaceService.addMarkup({
                ...mark,
                projectId: 'local',
                documentId: 'local',
                updatedAt: now,
            } as Markup);
        }

        trackLocalOpenSuccess(pageCount, file.size, importedMarks.length);
        return localViewerPath(file.name);
    } catch (err) {
        trackLocalOpenFailure(err);
        throw err;
    }
}
